/**
 * TanStack Query hooks for recent searches
 *
 * Provides cache-first recent searches with:
 * - Instant display on focus (no "Loading..." flicker)
 * - Optimistic updates on save
 * - IndexedDB persistence for logged-in users
 * - localStorage fallback for guests
 *
 * Supports all entity types: movie, tv, person, user, list
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';

/**
 * Supported entity types for recent searches
 */
export type RecentEntityType = 'movie' | 'tv' | 'person' | 'user' | 'list';

/**
 * Recent search item shape (normalized from API response)
 * - id: string to support both numeric IDs (movie/tv/person) and UUIDs (user/list)
 * - image_url: unified image field (overrides poster_path if present)
 * - subtitle: secondary text (e.g., "Actor" for person, "@username" for user)
 * - meta: entity-specific data (e.g., { username } for users)
 */
export interface RecentSearchItem {
  id: string;
  media_type: RecentEntityType;
  title: string;
  poster_path: string | null;
  image_url: string | null;
  subtitle: string | null;
  year: number | null;
  meta?: RecentSearchMeta;
}

/**
 * Metadata stored with recent searches (entity-specific data)
 */
export interface RecentSearchMeta {
  username?: string; // For user entities
  year?: number;     // For movie/tv (also stored at top level)
  followers_count?: number; // For user entities
  following_count?: number; // For user entities
  [key: string]: unknown;
}

/**
 * API response shape for recent searches (new column structure)
 */
interface RecentSearchesResponse {
  searches: Array<{
    entity_id: string;
    entity_type: RecentEntityType;
    title: string;
    poster_path: string | null;
    image_url: string | null;
    subtitle: string | null;
    year: number | null;
    meta: RecentSearchMeta;
    searched_at: string;
  }>;
}

/**
 * Normalize API response to RecentSearchItem[]
 */
function normalizeSearches(response: RecentSearchesResponse): RecentSearchItem[] {
  return (response.searches || []).map((s) => ({
    id: String(s.entity_id),
    media_type: s.entity_type,
    title: s.title,
    poster_path: s.poster_path,
    image_url: s.image_url,
    subtitle: s.subtitle,
    year: s.year,
    meta: s.meta || {},
  }));
}

/**
 * Hook to fetch recent searches for logged-in users
 *
 * - Enabled only when userId and token are present
 * - staleTime: 30 days (Spotify-grade: cache-first, minimal refetching)
 * - Persisted via queryPersistence.ts (bounded: max 6 items)
 */
export function useRecentSearches() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  return useQuery({
    queryKey: queryKeys.user.recentSearches(userId ?? ''),
    enabled: !!userId && !!token,
    queryFn: async ({ signal }) => {
      const response = await apiFetch<RecentSearchesResponse>('/user/recent-searches', {
        token: token!,
        signal,
      });
      return normalizeSearches(response);
    },
    staleTime: 30 * 1000, // 30s — rehydrated server-side, short window for count/avatar freshness
  });
}

/**
 * Mutation to save a recent search
 *
 * - Optimistic update: instantly adds to cache
 * - Dedupes recent saves (same item within 2s)
 * - Fire-and-forget POST (no blocking on response)
 */
export function useSaveRecentSearchMutation() {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (item: RecentSearchItem) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch('/user/recent-searches', {
        token,
        method: 'POST',
        body: {
          entity_id: item.id,
          entity_type: item.media_type,
          title: item.title,
          poster_path: item.poster_path,
          image_url: item.image_url,
          subtitle: item.subtitle,
          year: item.year,
          meta: item.meta || {},
        },
      });
    },
    onMutate: async (item: RecentSearchItem) => {
      if (!userId) return;

      const queryKey = queryKeys.user.recentSearches(userId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value
      const previousSearches = queryClient.getQueryData<RecentSearchItem[]>(queryKey);

      // Optimistic update: add to front, dedupe, cap at 6
      queryClient.setQueryData<RecentSearchItem[]>(queryKey, (old) => {
        const existing = old ?? [];
        const filtered = existing.filter(
          (r) => !(r.id === item.id && r.media_type === item.media_type)
        );
        return [item, ...filtered].slice(0, 6);
      });

      return { previousSearches };
    },
    onError: (_err, _item, context) => {
      // Rollback on error
      if (context?.previousSearches !== undefined && userId) {
        queryClient.setQueryData(
          queryKeys.user.recentSearches(userId),
          context.previousSearches
        );
      }
    },
    // No onSuccess invalidation needed - optimistic update is the source of truth
    // The server is fire-and-forget, so we trust the optimistic state
  });
}

// ============================================================================
// Guest localStorage helpers (used when user is not logged in)
// ============================================================================

const RECENT_SEARCHES_KEY = 'filmple:recent_searches:anon';
const MAX_LOCAL_RECENTS = 6;

/**
 * Get recent searches from localStorage (guest users)
 */
export function getLocalRecents(): RecentSearchItem[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save a recent search to localStorage (guest users)
 */
export function saveLocalRecent(item: RecentSearchItem): RecentSearchItem[] {
  try {
    const recents = getLocalRecents();
    // Remove duplicate if exists
    const filtered = recents.filter(
      (r) => !(r.id === item.id && r.media_type === item.media_type)
    );
    // Add to front, limit to max
    const updated = [item, ...filtered].slice(0, MAX_LOCAL_RECENTS);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
}
